import { ref } from 'vue'
import apiInstance from '@/utils/api'

export interface MediaFile {
  id: string
  fileName: string
  fileData: string
  type: 'image' | 'video'
  mediaId?: string | number
}

export interface PickupMonitor {
  id: string
  accountName: string
  password?: string
  status: 'active' | 'inactive'
  orderNumberSuffix?: string
  receiveOrdersFromMode?: 'familise' | 'bon' | ''
  selectedModes?: string[]
  displayScale?: number
  playbackEnabled?: boolean
  playbackType?: 'image' | 'video'
  slideDuration?: number
  mediaFiles?: MediaFile[]
  url: string
  accountLink?: string
  restaurantId: string
  createdAt?: string
  updatedAt?: string
  // Metadata about the third party API token. Null when none has been issued.
  // The token value itself is never returned by the API.
  integrationToken?: IntegrationTokenInfo | null
}

export interface IntegrationTokenInfo {
  createdAt: string | null
  lastUsedAt: string | null
}

const familiseModes = ref<Array<{ id: string | number; name: string }>>([])
const bonModes = ref<Array<{ id: string | number; name: string }>>([])

export interface PickupMonitorDraft extends Omit<PickupMonitor, 'id' | 'createdAt' | 'updatedAt' | 'url'> {
  validationError?: string
  showPassword?: boolean
  playbackType?: 'image' | 'video'
  slideDuration?: number
}

const createEmptyDraft = (): PickupMonitorDraft => ({
  accountName: '',
  password: '',
  status: 'active',
  orderNumberSuffix: '',
  receiveOrdersFromMode: '',
  selectedModes: [],
  displayScale: 3,
  playbackEnabled: false,
  playbackType: undefined,
  slideDuration: 5,
  mediaFiles: [],
  restaurantId: '',
  validationError: '',
  showPassword: false,
})

export const usePickupMonitors = (getRestaurantId: () => string) => {
  const monitors = ref<PickupMonitor[]>([])

  const draft = ref<PickupMonitorDraft>(createEmptyDraft())

  const initializeDraft = (monitorId?: string) => {
    if (monitorId) {
      const monitor = getMonitorById(monitorId)
      if (monitor) {
        draft.value = {
          accountName: monitor.accountName,
          password: monitor.password,
          status: monitor.status,
          orderNumberSuffix: monitor.orderNumberSuffix,
          receiveOrdersFromMode: monitor.receiveOrdersFromMode,
          selectedModes: [...(monitor.selectedModes || [])],
          displayScale: monitor.displayScale,
          playbackEnabled: monitor.playbackEnabled,
          playbackType: monitor.playbackType,
          slideDuration: monitor.slideDuration,
          mediaFiles: [...(monitor.mediaFiles || [])],
          restaurantId: monitor.restaurantId,
        }
      }
    } else {
      draft.value = createEmptyDraft()
    }
  }

  const updateDraft = (updates: Partial<PickupMonitorDraft>) => {
    draft.value = { ...draft.value, ...updates }
  }

  const getDraft = () => draft.value

  const clearDraft = () => {
    draft.value = createEmptyDraft()
  }

  const createMonitor = (data: Omit<PickupMonitor, 'id' | 'createdAt' | 'updatedAt'>): PickupMonitor => {
    const newMonitor: PickupMonitor = {
      ...data,
      id: Date.now().toString(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      url: `http://localhost:5000/pickup/${data.accountName.toLowerCase().replace(/\s+/g, '-')}`,
    }
    monitors.value.push(newMonitor)
    return newMonitor
  }

  const updateMonitor = (id: string, data: Partial<Omit<PickupMonitor, 'id' | 'createdAt' | 'accountName'>>) => {
    const index = monitors.value.findIndex((m) => m.id === id)
    if (index !== -1) {
      monitors.value[index] = {
        ...monitors.value[index],
        ...data,
        updatedAt: new Date().toISOString(),
      }
    }
  }

  const deleteMonitor = (id: string) => {
    const index = monitors.value.findIndex((m) => m.id === id)
    if (index !== -1) {
      monitors.value.splice(index, 1)
    }
  }

  const toggleMonitorStatus = (id: string) => {
    const monitor = monitors.value.find((m) => m.id === id)
    if (monitor) {
      monitor.status = monitor.status === 'active' ? 'inactive' : 'active'
      monitor.updatedAt = new Date().toISOString()
    }
  }

  const getMonitorById = (id: string) => {
    return monitors.value.find((m) => m.id === id)
  }

  const getRestaurantMonitors = (restaurantId: string) => {
    return monitors.value.filter((m) => m.restaurantId === restaurantId)
  }

  const getFamiliseModes = () => familiseModes.value

  const getBonModes = () => bonModes.value

  const getModes = (type: 'familise' | 'bon') => {
    return type === 'familise' ? familiseModes.value : bonModes.value
  }

  const loadSubCategories = async (lang?: string): Promise<void> => {
    const restaurantId = getRestaurantId()
    if (!restaurantId) {
      console.warn('⚠️ Restaurant ID is not available for loading sub-categories')
      return
    }

    try {
      const language = lang || 'en'

      const response = await apiInstance.get(`/restaurant/${restaurantId}/get-sub-categories?lang=${language}`)
      const data = response.data

      if (data.error === false && Array.isArray(data.data) && data.data.length > 0) {
        // Transform API response to mode objects
        familiseModes.value = data.data.map((item: any) => ({
          id: item.value.toString(),
          name: item.title,
        }))
      } else {
        console.warn('⚠️ No sub-categories available:', data.message || 'No data returned')
        // Clear the modes if no data is available
        familiseModes.value = []
      }
    } catch (e: any) {
      console.warn('⚠️ Could not load sub-categories:', e.message)
      // Clear the modes on error
      familiseModes.value = []
    }
  }

  const loadBonMonitors = async (): Promise<void> => {
    const restaurantId = getRestaurantId()
    if (!restaurantId) {
      console.warn('⚠️ Restaurant ID is not available for loading bon-monitors')
      return
    }

    try {

      const response = await apiInstance.get(`/restaurant/${restaurantId}/get-bon-monitors`)
      const data = response.data

      if (data.error === false && Array.isArray(data.data) && data.data.length > 0) {
        // Transform API response to mode objects
        bonModes.value = data.data.map((item: any) => ({
          id: item.value.toString(),
          name: item.title,
        }))
      } else {
        console.warn('⚠️ No bon-monitors available:', data.message || 'No data returned')
        // Clear the modes if no data is available
        bonModes.value = []
      }
    } catch (e: any) {
      console.warn('⚠️ Could not load bon-monitors:', e.message)
      // Clear the modes on error
      bonModes.value = []
    }
  }

  const loadPickupMonitorDetails = async (monitorId: string | number): Promise<PickupMonitor | null> => {
    const restaurantId = getRestaurantId()
    if (!restaurantId) {
      console.error('🔴 Restaurant ID is not available for loading monitor details')
      return null
    }

    if (!monitorId) {
      console.error('🔴 Monitor ID is not available')
      return null
    }

    try {

      const response = await apiInstance.get(`/restaurant/${restaurantId}/pickup-monitor/${monitorId}`)
      const data = response.data

      if (data.error === false && data.data) {
        const apiMonitor = data.data

        // Transform media array to MediaFile format
        const mediaFiles: MediaFile[] = (apiMonitor.media || []).map((media: any) => {
          const mimeType = media.mime_type || ''
          const fileType = mimeType.startsWith('image/') ? 'image' : 'video'

          return {
            id: `media-${media.id}-${Date.now()}`,
            fileName: media.original_url ? media.original_url.split('/').pop() || `media-${media.id}` : `media-${media.id}`,
            fileData: media.original_url, // Use URL as preview
            type: fileType as 'image' | 'video',
            mediaId: media.id,
          }
        })

        const monitor: PickupMonitor = {
          id: String(apiMonitor.id),
          accountName: apiMonitor.email || '',
          status: apiMonitor.status === 1 ? 'active' : 'inactive',
          url: apiMonitor.pum_auto_login || '',
          accountLink: apiMonitor.account_link || '',
          restaurantId: restaurantId,
          displayScale: apiMonitor.display_scale,
          playbackEnabled: apiMonitor.enable_playback === 1,
          playbackType: apiMonitor.playback_type as 'image' | 'video' | undefined,
          slideDuration: apiMonitor.slide_duration,
          orderNumberSuffix: apiMonitor.order_no_suffix,
          receiveOrdersFromMode: apiMonitor.item_cate_type as 'familise' | 'bon' | '' | any,
          selectedModes: (apiMonitor.sub_cate_ids || []).map((id: string | number) => String(id)),
          mediaFiles: mediaFiles,
          integrationToken: apiMonitor.integration_token
            ? {
                createdAt: apiMonitor.integration_token.created_at ?? null,
                lastUsedAt: apiMonitor.integration_token.last_used_at ?? null,
              }
            : null,
        }

        return monitor
      } else {
        console.warn('⚠️ Failed to load monitor details:', data.message || 'No data returned')
        return null
      }
    } catch (e: any) {
      console.error('🔴 Could not load monitor details:', e.message)
      return null
    }
  }

  const initializeRestaurantMonitors = (restaurantId: string) => {
    monitors.value.forEach((monitor) => {
      monitor.restaurantId = restaurantId
    })
  }

  const loadRestaurantPickupMonitors = async (restaurantId: string): Promise<void> => {
    try {
      if (!restaurantId) {
        console.warn('⚠️ Restaurant ID is required. Cannot load pickup monitors.')
        return
      }

      const response = await apiInstance.get(`/restaurant/${restaurantId}/pickup-monitor`)
      const data = response.data

      if (data.error === false && Array.isArray(data.data)) {
        // Transform API response to PickupMonitor objects
        const apiMonitors = data.data.map((apiMonitor: any) => ({
          id: apiMonitor.id.toString(),
          accountName: apiMonitor.email || '',
          status: apiMonitor.status === 1 ? 'active' : 'inactive',
          url: apiMonitor.pum_auto_login || '',
          accountLink: apiMonitor.account_link || '',
          restaurantId: restaurantId,
        } as PickupMonitor))

        // Clear existing monitors for this restaurant and add the fetched ones
        monitors.value = monitors.value.filter((m) => m.restaurantId !== restaurantId)
        monitors.value.push(...apiMonitors)

      } else {
        console.warn('⚠️ Failed to load pickup monitors:', data.message || 'No data returned')
      }
    } catch (e: any) {
      console.warn('⚠️ Could not load pickup monitors:', e.message)
    }
  }

  const uploadPickupMedia = async (files: File[], monitorId?: string | number): Promise<{ mediaId: string | number; fileName: string; fileData: string; type: 'image' | 'video' }[]> => {
    const restaurantId = getRestaurantId()
    if (!restaurantId) {
      console.error('🔴 Restaurant ID is not available for upload')
      return []
    }

    const results: { mediaId: string | number; fileName: string; fileData: string; type: 'image' | 'video' }[] = []

    for (const file of files) {
      try {
        const formData = new FormData()
        formData.append('type', 'pickup')
        formData.append('media[]', file)

        // Add type_id if monitorId is provided (edit mode only)
        if (monitorId) {
          formData.append('type_id', monitorId.toString())
        }

        const response = await apiInstance.post(`/restaurant/${restaurantId}/upload-media`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })

        const data = response.data

        if (data.error === false && data.data) {
          let mediaId: string | number | null = null
          let mediaUrl: string | null = null

          // Handle the new response format where media_ids contains objects
          if (data.data.media_ids && Array.isArray(data.data.media_ids) && data.data.media_ids.length > 0) {
            const mediaItem = data.data.media_ids[0] // Assuming only one media item is uploaded at a time
            if (typeof mediaItem === 'object' && mediaItem.id) {
              // New format: {id, mime_type, original_url}
              mediaId = mediaItem.id
              mediaUrl = mediaItem.original_url
            } else {
              // Fallback for older format where it's just the ID
              mediaId = mediaItem
            }
          } else if (data.data.id) {
            mediaId = data.data.id
          } else if (data.data.media_id) {
            mediaId = data.data.media_id
          }

          if (mediaId) {
            const fileType = file.type.startsWith('image/') ? 'image' : 'video'

            // Use original_url from API response if available, otherwise read file as base64
            if (mediaUrl) {
              results.push({
                mediaId: mediaId,
                fileName: file.name,
                fileData: mediaUrl, // Use original_url for preview
                type: fileType,
              })
            } else {
              // Fallback: read file as base64 if original_url is not available
              const reader = new FileReader()
              await new Promise((resolve) => {
                reader.onload = () => {
                  results.push({
                    mediaId: mediaId!,
                    fileName: file.name,
                    fileData: reader.result as string,
                    type: fileType,
                  })
                  resolve(true)
                }
                reader.readAsDataURL(file)
              })
            }
          }
        } else {
          console.warn('⚠️ Upload response indicates failure or missing media_ids:', data)
        }
      } catch (error: any) {
        console.error('🔴 Error uploading media:', error)
        if (error.response) {
          console.error('🔴 API Error Response:', error.response.status, error.response.data)
        }
        throw error
      }
    }

    return results
  }

  const deletePickupMedia = async (mediaId: string | number, monitorId?: string | number): Promise<boolean> => {
    const restaurantId = getRestaurantId()
    if (!restaurantId) {
      console.error('🔴 Restaurant ID is not available for media deletion')
      return false
    }

    if (!mediaId) {
      console.error('🔴 Media ID is not available for deletion')
      return false
    }

    try {
      const payload: any = {
        type: 'pickup',
      }

      if (monitorId) {
        payload.type_id = monitorId
      }

      const response = await apiInstance.delete(`/restaurant/${restaurantId}/delete-media/${mediaId}`, {
        data: payload,
      })

      const data = response.data

      if (data.error === false || response.status === 200 || response.status === 204) {
        // Remove from draft if it exists
        const currentFiles = draft.value.mediaFiles || []
        draft.value.mediaFiles = currentFiles.filter((f) => f.mediaId !== mediaId)
        return true
      }

      console.warn('⚠️ Delete response indicates failure:', data)
      return false
    } catch (error) {
      console.error('🔴 Error deleting media:', error)
      return false
    }
  }

  const createPickupMonitorAPI = async (formData: any): Promise<{ success: boolean; data?: any; errors?: any }> => {
    const restaurantId = getRestaurantId()
    if (!restaurantId) {
      console.error('🔴 Restaurant ID is not available for creating monitor')
      return { success: false, errors: { general: ['Restaurant ID is not available'] } }
    }

    try {
      // Map form data to API payload
      const payload: any = {
        email: formData.accountName,
        password: formData.password,
        status: formData.status === 'active' ? '1' : '0',
        order_no_suffix: formData.orderNumberSuffix,
        item_cate_type: formData.receiveOrdersFromMode,
        display_scale: parseInt(String(formData.displayScale)),
        enable_playback: formData.playbackEnabled ? 1 : 0,
      }

      // Only add sub_cate_ids if not "All"
      if (formData.receiveOrdersFromMode !== 'All') {
        payload.sub_cate_ids = (formData.selectedModes || []).map((id: string | number) => {
          const num = Number(id)
          return isNaN(num) ? id : num
        })
      }

      // Only add playback_type and slide_duration if playback is enabled
      if (formData.playbackEnabled) {
        payload.playback_type = formData.playbackType
        if (formData.playbackType === 'image') {
          payload.slide_duration = parseInt(String(formData.slideDuration || 5))
        }
      }

      // Add media_ids if there are media files with mediaId
      if (formData.mediaFiles && formData.mediaFiles.length > 0) {
        const mediaIds = formData.mediaFiles
          .filter((f: any) => f.mediaId)
          .map((f: any) => {
            const num = Number(f.mediaId)
            return isNaN(num) ? f.mediaId : num
          })
        if (mediaIds.length > 0) {
          payload.media_ids = mediaIds
        }
      }

      const response = await apiInstance.post(`/restaurant/${restaurantId}/pickup-monitor`, payload)
      const responseData = response.data

      if (responseData.error === false && responseData.data) {
        const newMonitor = responseData.data
        // Add to local store
        monitors.value.push({
          id: String(newMonitor.id),
          accountName: newMonitor.email || '',
          status: newMonitor.status === 1 ? 'active' : 'inactive',
          url: '',
          restaurantId: restaurantId,
          displayScale: newMonitor.display_scale,
          playbackEnabled: newMonitor.enable_playback === 1,
          playbackType: newMonitor.playback_type as 'image' | 'video' | undefined,
          slideDuration: newMonitor.slide_duration,
          orderNumberSuffix: newMonitor.order_no_suffix,
          receiveOrdersFromMode: newMonitor.item_cate_type as 'familise' | 'bon' | '',
          selectedModes: newMonitor.sub_cate_ids || [],
        })
        return { success: true, data: newMonitor }
      }

      if (responseData.errors) {
        console.warn('⚠️ Validation errors:', responseData.errors)
        return { success: false, errors: responseData.errors }
      }

      console.warn('⚠️ Create response indicates failure:', responseData.message)
      return { success: false, errors: { general: [responseData.message] } }
    } catch (error: any) {
      console.error('🔴 Error creating monitor:', error.message)
      console.error('🔴 Error status:', error.response?.status)
      console.error('🔴 Error response data:', JSON.stringify(error.response?.data, null, 2))

      // Handle 422 Unprocessable Entity (validation errors)
      if (error.response?.status === 422) {
        const responseData = error.response?.data
        console.warn('⚠️ Validation error response:', JSON.stringify(responseData, null, 2))

        if (responseData?.errors) {
          return { success: false, errors: responseData.errors }
        }
        if (responseData?.message) {
          return { success: false, errors: { general: [responseData.message] } }
        }
        if (responseData?.error === true) {
          return { success: false, errors: { general: [responseData.message || 'Validation error'] } }
        }
      }

      // Handle 404 and other error responses with error flag
      if (error.response?.data?.error === true && error.response?.data?.message) {
        return { success: false, errors: { general: [error.response.data.message] } }
      }

      // Handle other error responses
      if (error.response?.data?.errors) {
        return { success: false, errors: error.response.data.errors }
      }

      if (error.response?.data?.message) {
        return { success: false, errors: { general: [error.response.data.message] } }
      }

      console.error('🔴 Full error details:', JSON.stringify(error.response?.data, null, 2) || error.message)
      return { success: false, errors: { general: [error.message || 'Failed to create pickup monitor'] } }
    }
  }

  const updatePickupMonitorAPI = async (monitorId: string | number, formData: any): Promise<{ success: boolean; data?: any; errors?: any }> => {
    const restaurantId = getRestaurantId()
    if (!restaurantId) {
      console.error('🔴 Restaurant ID is not available for updating monitor')
      return { success: false, errors: { general: ['Restaurant ID is not available'] } }
    }

    if (!monitorId) {
      console.error('🔴 Monitor ID is not available for updating')
      return { success: false, errors: { general: ['Monitor ID is not available'] } }
    }

    try {
      // Map form data to API payload
      // Note: email is NOT included in update payload
      const payload: any = {
        status: formData.status === 'active' ? '1' : '0',
        order_no_suffix: formData.orderNumberSuffix,
        item_cate_type: formData.receiveOrdersFromMode || 'All',
        display_scale: parseInt(String(formData.displayScale || 3)),
        enable_playback: formData.playbackEnabled ? 1 : 0,
      }

      // Only add password if it's provided and non-blank
      if (formData.password && formData.password.trim()) {
        payload.password = formData.password
      }

      // Only add sub_cate_ids if not "All"
      if (formData.receiveOrdersFromMode !== 'All') {
        payload.sub_cate_ids = (formData.selectedModes || []).map((id: string | number) => {
          const num = Number(id)
          return isNaN(num) ? id : num
        })
      }

      // Only add playback_type and slide_duration if playback is enabled
      if (formData.playbackEnabled) {
        payload.playback_type = formData.playbackType
        if (formData.playbackType === 'image') {
          payload.slide_duration = parseInt(String(formData.slideDuration || 5))
        }
      }

      // Add media_ids if there are media files with mediaId
      if (formData.mediaFiles && formData.mediaFiles.length > 0) {
        const mediaIds = formData.mediaFiles
          .filter((f: any) => f.mediaId)
          .map((f: any) => {
            const num = Number(f.mediaId)
            return isNaN(num) ? f.mediaId : num
          })
        if (mediaIds.length > 0) {
          payload.media_ids = mediaIds
        }
      }

      const response = await apiInstance.put(`/restaurant/${restaurantId}/pickup-monitor/${monitorId}`, payload)
      const responseData = response.data

      if (responseData.error === false && responseData.data) {
        const updatedMonitor = responseData.data
        // Update in local store
        const index = monitors.value.findIndex((m) => m.id === String(monitorId))
        if (index !== -1) {
          monitors.value[index] = {
            ...monitors.value[index],
            status: updatedMonitor.status === 1 ? 'active' : 'inactive',
            displayScale: updatedMonitor.display_scale,
            playbackEnabled: updatedMonitor.enable_playback === 1,
            playbackType: updatedMonitor.playback_type as 'image' | 'video' | undefined,
            slideDuration: updatedMonitor.slide_duration,
            orderNumberSuffix: updatedMonitor.order_no_suffix,
            receiveOrdersFromMode: updatedMonitor.item_cate_type as 'familise' | 'bon' | '',
            selectedModes: updatedMonitor.sub_cate_ids || [],
            updatedAt: new Date().toISOString(),
          }
        }
        return { success: true, data: updatedMonitor }
      }

      if (responseData.errors) {
        console.warn('⚠️ Validation errors:', responseData.errors)
        return { success: false, errors: responseData.errors }
      }

      console.warn('⚠️ Update response indicates failure:', responseData.message)
      return { success: false, errors: { general: [responseData.message] } }
    } catch (error: any) {
      console.error('🔴 Error updating monitor:', error.message)
      console.error('🔴 Error status:', error.response?.status)
      console.error('🔴 Error response data:', JSON.stringify(error.response?.data, null, 2))

      // Handle 422 Unprocessable Entity (validation errors)
      if (error.response?.status === 422) {
        const responseData = error.response?.data
        console.warn('⚠️ Validation error response:', JSON.stringify(responseData, null, 2))

        if (responseData?.errors) {
          return { success: false, errors: responseData.errors }
        }
        if (responseData?.message) {
          return { success: false, errors: { general: [responseData.message] } }
        }
        if (responseData?.error === true) {
          return { success: false, errors: { general: [responseData.message || 'Validation error'] } }
        }
      }

      // Handle 404 and other error responses with error flag
      if (error.response?.data?.error === true && error.response?.data?.message) {
        return { success: false, errors: { general: [error.response.data.message] } }
      }

      // Handle other error responses
      if (error.response?.data?.errors) {
        return { success: false, errors: error.response.data.errors }
      }

      if (error.response?.data?.message) {
        return { success: false, errors: { general: [error.response.data.message] } }
      }

      console.error('🔴 Full error details:', JSON.stringify(error.response?.data, null, 2) || error.message)
      return { success: false, errors: { general: [error.message || 'Failed to update pickup monitor'] } }
    }
  }

  // Get the next suggested account name (<restaurant_domain>-pum-<n>) from the API
  const suggestAccountName = async (restaurantId: string): Promise<string | null> => {
    try {
      const response = await apiInstance.get(`/restaurant/${restaurantId}/pickup-monitor/suggest-account-name`)
      const data = response.data

      if (data.error === false && data.data?.account_name) {
        return data.data.account_name as string
      }

      return null
    } catch (e: any) {
      console.warn('⚠️ Could not suggest pickup monitor account name:', e.message)
      return null
    }
  }

  // Issue a never expiring API token for third party software. The plain token is
  // returned once and only its hash is stored, so it cannot be read back later.
  // Generating again revokes the previous one.
  const generateIntegrationToken = async (
    monitorId: string | number
  ): Promise<{
    success: boolean
    token: string | null
    createdAt: string | null
    replacedPrevious: boolean
    message: string | null
  }> => {
    const restaurantId = getRestaurantId()
    if (!restaurantId) {
      console.error('🔴 Restaurant ID is not available for generating an integration token')
      return { success: false, token: null, createdAt: null, replacedPrevious: false, message: 'Restaurant ID is not available' }
    }

    if (!monitorId) {
      console.error('🔴 Monitor ID is not available for generating an integration token')
      return { success: false, token: null, createdAt: null, replacedPrevious: false, message: 'Monitor ID is not available' }
    }

    try {
      const response = await apiInstance.post(
        `/restaurant/${restaurantId}/pickup-monitor/${monitorId}/generate-third-party-token`
      )
      const data = response.data

      if (data.error === true || !data.data?.token) {
        return {
          success: false,
          token: null,
          createdAt: null,
          replacedPrevious: false,
          message: data.message || 'Failed to generate token',
        }
      }

      return {
        success: true,
        token: data.data.token as string,
        createdAt: (data.data.created_at as string) ?? null,
        replacedPrevious: Boolean(data.data.replaced_previous),
        message: null,
      }
    } catch (e: any) {
      console.error('🔴 Error generating integration token:', e.message)
      return {
        success: false,
        token: null,
        createdAt: null,
        replacedPrevious: false,
        message: e.response?.data?.message || 'Network error.',
      }
    }
  }

  return {
    monitors,
    draft,
    createMonitor,
    createPickupMonitorAPI,
    updatePickupMonitorAPI,
    generateIntegrationToken,
    suggestAccountName,
    updateMonitor,
    deleteMonitor,
    toggleMonitorStatus,
    getMonitorById,
    getRestaurantMonitors,
    getFamiliseModes,
    getBonModes,
    getModes,
    loadSubCategories,
    loadBonMonitors,
    loadPickupMonitorDetails,
    initializeRestaurantMonitors,
    loadRestaurantPickupMonitors,
    uploadPickupMedia,
    deletePickupMedia,
    initializeDraft,
    updateDraft,
    getDraft,
    clearDraft,
  }
}
